// MSE playback of the VMS's endless fMP4, for browsers without captureStream().
// WebKit won't play it as <video src> (no Range support), so we append it ourselves.

type BoxVisitor = (type: string, start: number, end: number) => void;
type Bytes = Uint8Array<ArrayBuffer>;

function fourcc(buf: Uint8Array, at: number): string {
    return String.fromCharCode(buf[at], buf[at + 1], buf[at + 2], buf[at + 3]);
}

/** Walk the boxes in buf[start, end); stops at a malformed size. */
function walkBoxes(
    buf: Uint8Array,
    start: number,
    end: number,
    visit: BoxVisitor,
): void {
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    let at = start;
    while (at + 8 <= end) {
        let size = view.getUint32(at);
        let header = 8;
        if (size === 1) {
            if (at + 16 > end) return;
            size = Number(view.getBigUint64(at + 8));
            header = 16;
        } else if (size === 0) {
            size = end - at;
        }
        if (size < header || at + size > end) return;
        visit(fourcc(buf, at + 4), at + header, at + size);
        at += size;
    }
}

function findBox(
    buf: Uint8Array,
    start: number,
    end: number,
    path: string[],
): [number, number] | null {
    let found: [number, number] | null = null;
    walkBoxes(buf, start, end, (type, s, e) => {
        if (found || type !== path[0]) return;
        found = path.length === 1 ? [s, e] : findBox(buf, s, e, path.slice(1));
    });
    return found;
}

const hex2 = (n: number): string => n.toString(16).padStart(2, "0");

function reverseBits32(n: number): number {
    let out = 0;
    for (let i = 0; i < 32; i++) out = (out << 1) | ((n >>> i) & 1);
    return out >>> 0;
}

/** RFC 6381 codec string for one sample entry, or null if unrecognised. */
function sampleEntryCodec(
    buf: Uint8Array,
    type: string,
    start: number,
    end: number,
): string | null {
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);

    if (type === "avc1" || type === "avc3") {
        // 78 bytes of VisualSampleEntry fields precede the child boxes.
        const avcC = findBox(buf, start + 78, end, ["avcC"]);
        if (!avcC) return type;
        const [s] = avcC;
        return `${type}.${hex2(buf[s + 1])}${hex2(buf[s + 2])}${hex2(buf[s + 3])}`;
    }

    if (type === "hvc1" || type === "hev1") {
        const hvcC = findBox(buf, start + 78, end, ["hvcC"]);
        if (!hvcC) return type;
        const [s] = hvcC;
        const b1 = buf[s + 1];
        const space = ["", "A", "B", "C"][b1 >> 6];
        const tier = b1 & 0x20 ? "H" : "L";
        const compat = reverseBits32(view.getUint32(s + 2)).toString(16);
        const constraints = Array.from(buf.subarray(s + 6, s + 12));
        while (
            constraints.length &&
            constraints[constraints.length - 1] === 0
        ) {
            constraints.pop();
        }
        const suffix = constraints.map((b) => `.${b.toString(16)}`).join("");
        return `${type}.${space}${b1 & 0x1f}.${compat}.${tier}${buf[s + 12]}${suffix}`;
    }

    if (type === "mp4a") {
        // 28 bytes of AudioSampleEntry fields precede the child boxes.
        const esds = findBox(buf, start + 28, end, ["esds"]);
        if (!esds) return "mp4a.40.2";
        let at = esds[0] + 4;
        const readLen = (): number => {
            let len = 0;
            for (let i = 0; i < 4; i++) {
                const b = buf[at++];
                len = (len << 7) | (b & 0x7f);
                if (!(b & 0x80)) break;
            }
            return len;
        };
        if (buf[at++] !== 0x03) return "mp4a.40.2";
        readLen();
        at += 2;
        const flags = buf[at++];
        if (flags & 0x80) at += 2;
        if (flags & 0x40) at += 1 + buf[at];
        if (flags & 0x20) at += 2;
        if (buf[at++] !== 0x04) return "mp4a.40.2";
        readLen();
        const oti = buf[at];
        at += 13;
        if (buf[at++] !== 0x05) return `mp4a.${oti.toString(16)}`;
        readLen();
        return `mp4a.${oti.toString(16)}.${buf[at] >> 3}`;
    }

    return null;
}

interface InitInfo {
    mime: string;
    /** Audio/video sample entries with no codec string; MSE would reject the init. */
    unsupported: string[];
    videoTrackId: number | null;
    /** trex default_sample_flags per track, for fragments that omit their own. */
    trexFlags: Map<number, number>;
}

function parseInit(init: Uint8Array): InitInfo | null {
    const moov = findBox(init, 0, init.length, ["moov"]);
    if (!moov) return null;
    const view = new DataView(init.buffer, init.byteOffset, init.byteLength);

    const codecs: string[] = [];
    const unsupported: string[] = [];
    let hasVideo = false;
    let videoTrackId: number | null = null;
    const trexFlags = new Map<number, number>();

    walkBoxes(init, moov[0], moov[1], (type, s, e) => {
        if (type === "mvex") {
            walkBoxes(init, s, e, (t, ts) => {
                if (t === "trex")
                    trexFlags.set(
                        view.getUint32(ts + 4),
                        view.getUint32(ts + 20),
                    );
            });
            return;
        }
        if (type !== "trak") return;

        const tkhd = findBox(init, s, e, ["tkhd"]);
        const hdlr = findBox(init, s, e, ["mdia", "hdlr"]);
        const stsd = findBox(init, s, e, ["mdia", "minf", "stbl", "stsd"]);
        if (!stsd) return;

        const handler = hdlr ? fourcc(init, hdlr[0] + 8) : "";
        // stsd is a FullBox with an entry count before its entries.
        walkBoxes(init, stsd[0] + 8, stsd[1], (entry, es, ee) => {
            const codec = sampleEntryCodec(init, entry, es, ee);
            if (codec) codecs.push(codec);
            // e.g. G.711 or Opus audio: left out of the codecs string, it fails at append.
            else if (handler === "vide" || handler === "soun")
                unsupported.push(entry);
        });

        if (handler === "vide" && tkhd && videoTrackId === null) {
            hasVideo = true;
            // tkhd v1 has 64-bit times before track_ID.
            const version = init[tkhd[0]];
            videoTrackId = view.getUint32(tkhd[0] + (version === 1 ? 20 : 12));
        }
    });

    if (!codecs.length || !hasVideo) return null;
    return {
        mime: `video/mp4; codecs="${codecs.join(",")}"`,
        unsupported,
        videoTrackId,
        trexFlags,
    };
}

const SAMPLE_IS_NON_SYNC = 0x10000;

/** Whether a moof's video run starts on a sync sample. No video traf reads as false. */
function fragmentStartsWithKeyframe(moof: Uint8Array, info: InitInfo): boolean {
    const view = new DataView(moof.buffer, moof.byteOffset, moof.byteLength);
    const top = findBox(moof, 0, moof.length, ["moof"]);
    if (!top) return true;

    let result: boolean | null = null;
    walkBoxes(moof, top[0], top[1], (type, s, e) => {
        if (result !== null || type !== "traf") return;
        const tfhd = findBox(moof, s, e, ["tfhd"]);
        if (!tfhd) return;
        const trackId = view.getUint32(tfhd[0] + 4);
        if (info.videoTrackId !== null && trackId !== info.videoTrackId) return;

        const trun = findBox(moof, s, e, ["trun"]);
        if (!trun) {
            result = true;
            return;
        }

        let defaultFlags = info.trexFlags.get(trackId);
        const tfFlags = view.getUint32(tfhd[0]) & 0xffffff;
        if (tfFlags & 0x20) {
            let at = tfhd[0] + 8;
            if (tfFlags & 0x01) at += 8;
            if (tfFlags & 0x02) at += 4;
            if (tfFlags & 0x08) at += 4;
            if (tfFlags & 0x10) at += 4;
            defaultFlags = view.getUint32(at);
        }

        const trFlags = view.getUint32(trun[0]) & 0xffffff;
        let at = trun[0] + 8;
        if (trFlags & 0x01) at += 4;
        let flags: number | undefined;
        if (trFlags & 0x04) {
            flags = view.getUint32(at);
        } else if (trFlags & 0x400) {
            if (trFlags & 0x100) at += 4;
            if (trFlags & 0x200) at += 4;
            flags = view.getUint32(at);
        } else {
            flags = defaultFlags;
        }
        result = flags === undefined ? true : !(flags & SAMPLE_IS_NON_SYNC);
    });
    // Audio-only fragments must not reset the GOP backlog.
    return result ?? false;
}

type Segment =
    | { kind: "init"; data: Bytes }
    | { kind: "fragment"; data: Bytes };

/** Cuts a byte stream into the init segment and whole moof+mdat fragments. */
export class Fmp4Splitter {
    private chunks: Uint8Array[] = [];
    private length = 0;
    // Bytes needed before another parse can make progress; avoids re-merging per chunk.
    private need = 8;
    private sawInit = false;
    // Offset of the first unparsed box; boxes before it await their mdat.
    private parsedTo = 0;

    push(chunk: Uint8Array): Segment[] {
        this.chunks.push(chunk);
        this.length += chunk.length;
        if (this.length < this.need) return [];

        const buf =
            this.chunks.length === 1
                ? this.chunks[0]
                : new Uint8Array(this.length);
        if (this.chunks.length > 1) {
            let offset = 0;
            for (const c of this.chunks) {
                buf.set(c, offset);
                offset += c.length;
            }
        }

        const out: Segment[] = [];
        const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
        let at = this.parsedTo;
        let cut = 0;
        this.need = 0;

        while (at + 8 <= buf.length) {
            let size = view.getUint32(at);
            if (size === 1) {
                if (at + 16 > buf.length) {
                    this.need = at + 16;
                    break;
                }
                size = Number(view.getBigUint64(at + 8));
            }
            if (size < 8) throw new Error("Malformed camera stream");
            if (at + size > buf.length) {
                this.need = at + size;
                break;
            }

            const type = fourcc(buf, at + 4);
            at += size;

            if (!this.sawInit && type === "moov") {
                out.push({ kind: "init", data: buf.slice(cut, at) });
                this.sawInit = true;
                cut = at;
            } else if (this.sawInit && type === "mdat") {
                out.push({ kind: "fragment", data: buf.slice(cut, at) });
                cut = at;
            }
        }

        const rest = buf.subarray(cut);
        this.chunks = rest.length ? [rest] : [];
        this.length = rest.length;
        this.parsedTo = at - cut;
        this.need = Math.max(this.need - cut, this.parsedTo + 8);
        return out;
    }
}

type MediaSourceCtor = typeof MediaSource;

function mediaSourceCtor(): MediaSourceCtor | null {
    const w = window as unknown as {
        ManagedMediaSource?: MediaSourceCtor;
        MediaSource?: MediaSourceCtor;
    };
    // iPhone only has ManagedMediaSource; it also cooperates with iPad power management.
    return w.ManagedMediaSource ?? w.MediaSource ?? null;
}

export function isMseSupported(): boolean {
    return mediaSourceCtor() !== null;
}

// Live edge: catch up when this far behind, landing this far behind. The target
// sits above one fragment's duration (~1s here) or playback stalls per fragment.
const LIVE_MAX_LATENCY_S = 5;
const LIVE_TARGET_LATENCY_S = 2;
const KEEP_BEHIND_S = 10;
const LIVE_MAX_BUFFER_S = 30;
const MAINTAIN_INTERVAL_MS = 1000;
// A player that stops accepting appends must not grow its queue forever.
const MAX_QUEUED_FRAGMENTS = 120;
const MAX_GOP_BYTES = 4 * 1024 * 1024;

// Players whose currentTime the feed itself just set, so they can skip the seek UI.
const feedSeeks = new WeakSet<HTMLVideoElement>();

/** Whether the current seek on `video` was made by the feed, not the viewer. */
export function isFeedSeeking(video: HTMLVideoElement): boolean {
    return feedSeeks.has(video);
}

function rangeIndexAt(ranges: TimeRanges, t: number): number {
    for (let i = 0; i < ranges.length; i++) {
        if (t >= ranges.start(i) && t < ranges.end(i)) return i;
    }
    return -1;
}

class FeedSink {
    private readonly feed: MseFeed;
    private readonly video: HTMLVideoElement;
    private readonly source: MediaSource;
    private readonly url: string;
    private buffer: SourceBuffer | null = null;
    private queue: Bytes[];
    // queue[0] is the init segment until its append succeeds.
    private initQueued = true;
    // After dropping media, skip fragments until one can be decoded on its own.
    private awaitingKeyframe = false;
    private timer: number | null = null;
    private closed = false;
    private progressAt = Date.now();
    private lastTime = -1;
    private lastFrames = -1;

    constructor(feed: MseFeed, video: HTMLVideoElement, backlog: Bytes[]) {
        this.feed = feed;
        this.video = video;
        this.queue = backlog;
        const Ctor = mediaSourceCtor()!;
        this.source = new Ctor();
        // ManagedMediaSource never opens unless AirPlay is ruled out.
        video.disableRemotePlayback = true;
        this.url = URL.createObjectURL(this.source);
        this.source.addEventListener("sourceopen", this.onOpen, { once: true });
        video.src = this.url;
    }

    private onOpen = (): void => {
        if (this.closed) return;
        try {
            this.buffer = this.source.addSourceBuffer(this.feed.mime);
        } catch (error) {
            console.error("[MSE feed] addSourceBuffer failed:", error);
            return;
        }
        if (this.feed.live) {
            // Native controls show a live broadcast instead of a growing timeline.
            // Live catch-up undoes scrubbing back, as Chrome can't scrub live either.
            try {
                this.source.duration = Infinity;
            } catch {
                // Cosmetic only.
            }
        }
        this.buffer.addEventListener("updateend", this.onUpdateEnd);
        this.timer = window.setInterval(this.maintain, MAINTAIN_INTERVAL_MS);
        this.pump();
    };

    private onUpdateEnd = (): void => {
        this.maintain();
        this.pump();
    };

    /** Queue a fragment; null only re-runs the pump, e.g. to end the stream. */
    enqueue(fragment: Bytes | null, isKeyframe = false): void {
        if (this.closed) return;
        if (fragment) {
            if (this.queue.length >= MAX_QUEUED_FRAGMENTS) this.dropMedia();
            if (isKeyframe) this.awaitingKeyframe = false;
            if (!this.awaitingKeyframe) this.queue.push(fragment);
        }
        this.pump();
    }

    /** Drop queued media; cutting mid-GOP would leave frames with missing references. */
    private dropMedia(): void {
        this.queue = this.initQueued ? this.queue.slice(0, 1) : [];
        this.awaitingKeyframe = true;
    }

    private pump(): void {
        const sb = this.buffer;
        if (this.closed || !sb || sb.updating) return;
        if (this.source.readyState !== "open") return;
        if (!this.queue.length) {
            // Lets a recorded clip play out to 'ended' instead of waiting for more.
            if (this.feed.ended) {
                try {
                    this.source.endOfStream();
                } catch {
                    // Already ended.
                }
            }
            return;
        }
        try {
            sb.appendBuffer(this.queue[0]);
            this.queue.shift();
            this.initQueued = false;
        } catch (error) {
            if ((error as DOMException).name === "QuotaExceededError") {
                // Retried on updateend once the eviction lands.
                if (!this.evict(true)) this.dropMedia();
                return;
            }
            console.error("[MSE feed] appendBuffer failed:", error);
            this.queue = [];
        }
    }

    get playing(): boolean {
        return !this.video.paused;
    }

    /** Seconds buffered past the playhead, for paced (recorded) reading. */
    aheadSeconds(): number {
        const sb = this.buffer;
        if (!sb || this.closed) return 0;
        try {
            const ranges = sb.buffered;
            if (!ranges.length) return 0;
            return Math.max(
                0,
                ranges.end(ranges.length - 1) - this.video.currentTime,
            );
        } catch {
            return 0;
        }
    }

    private evict(force: boolean): boolean {
        const sb = this.buffer;
        if (!sb || sb.updating) return false;
        // Both throw once the buffer is detached from its MediaSource.
        try {
            const ranges = sb.buffered;
            if (!ranges.length) return false;

            const start = ranges.start(0);
            const end = ranges.end(ranges.length - 1);
            let removeTo = this.video.currentTime - KEEP_BEHIND_S;
            // A paused live tile keeps receiving; it jumps to the edge on resume anyway.
            if (this.feed.live)
                removeTo = Math.max(removeTo, end - LIVE_MAX_BUFFER_S);
            if (force)
                removeTo = Math.max(removeTo, this.video.currentTime - 1);
            if (removeTo - start < (force ? 0.5 : 5)) return false;

            sb.remove(start, removeTo);
            return true;
        } catch {
            return false;
        }
    }

    /** How long this player has been meant to play without moving, in ms. */
    stalledMs(): number {
        return this.closed ? 0 : Date.now() - this.progressAt;
    }

    private trackProgress(): void {
        const video = this.video;
        const t = video.currentTime;
        const frames = video.getVideoPlaybackQuality?.().totalVideoFrames ?? 0;
        if (video.paused || t !== this.lastTime || frames !== this.lastFrames) {
            this.lastTime = t;
            this.lastFrames = frames;
            this.progressAt = Date.now();
        }
    }

    private maintain = (): void => {
        const sb = this.buffer;
        const video = this.video;
        if (this.closed || !sb) return;
        this.trackProgress();
        if (video.seeking) return;

        let ranges: TimeRanges;
        try {
            ranges = sb.buffered;
        } catch {
            return;
        }
        if (!ranges.length) return;

        const last = ranges.length - 1;
        const end = ranges.end(last);
        const t = video.currentTime;
        const index = rangeIndexAt(ranges, t);

        if (index === -1) {
            // VMS timestamps start far from 0, and gaps strand the playhead.
            // Past the end it is just waiting for the next fragment.
            let next = -1;
            for (let i = 0; i < ranges.length; i++) {
                if (ranges.start(i) > t) {
                    next = i;
                    break;
                }
            }
            if (next !== -1) {
                this.seek(
                    this.feed.live
                        ? Math.max(
                              ranges.start(last),
                              end - LIVE_TARGET_LATENCY_S,
                          )
                        : ranges.start(next),
                );
            }
        } else if (
            this.feed.live &&
            !video.paused &&
            end - t > LIVE_MAX_LATENCY_S
        ) {
            this.seek(end - LIVE_TARGET_LATENCY_S);
        }

        this.evict(false);
    };

    private seek(t: number): void {
        feedSeeks.add(this.video);
        this.video.addEventListener("seeked", this.onSeeked, { once: true });
        this.video.currentTime = t;
    }

    private onSeeked = (): void => {
        feedSeeks.delete(this.video);
    };

    close(): void {
        if (this.closed) return;
        this.closed = true;
        if (this.timer !== null) clearInterval(this.timer);
        this.buffer?.removeEventListener("updateend", this.onUpdateEnd);
        this.video.removeEventListener("seeked", this.onSeeked);
        feedSeeks.delete(this.video);
        this.queue = [];
        // Detach first so the player ignores the error load() can raise.
        if (this.video.getAttribute("src") === this.url) {
            this.video.removeAttribute("src");
            this.video.load();
        }
        URL.revokeObjectURL(this.url);
    }
}

/**
 * One VMS session's bytes, fanned out to every player showing that camera.
 * Created per session: a reopen is a new init segment and new timestamps.
 */
export class MseFeed {
    readonly mime: string;
    readonly live: boolean;
    /** The server finished sending (a recorded clip); players play out what they have. */
    ended = false;
    private readonly init: Bytes;
    private readonly info: InitInfo;
    // Fragments since the last keyframe, so a late player starts on a picture.
    private gop: Bytes[] = [];
    private gopBytes = 0;
    // A truncated backlog can't be decoded, so past the cap joiners wait for a keyframe.
    private gopDropped = false;
    private readonly sinks = new Set<FeedSink>();
    private closed = false;

    private constructor(init: Bytes, info: InitInfo, live: boolean) {
        this.init = init;
        this.info = info;
        this.mime = info.mime;
        this.live = live;
    }

    /** Null when the init segment is unreadable or its codecs unsupported. */
    static fromInit(init: Bytes, live: boolean): MseFeed | null {
        const info = parseInit(init);
        const Ctor = mediaSourceCtor();
        if (
            !info ||
            !Ctor ||
            info.unsupported.length ||
            !Ctor.isTypeSupported(info.mime)
        ) {
            console.error(
                "[MSE feed] Unsupported stream:",
                !info
                    ? "unreadable init segment"
                    : info.unsupported.length
                      ? `sample entry ${info.unsupported.join(", ")}`
                      : info.mime,
            );
            return null;
        }
        return new MseFeed(init, info, live);
    }

    pushFragment(fragment: Bytes): void {
        if (this.closed) return;
        const isKeyframe = fragmentStartsWithKeyframe(fragment, this.info);
        if (isKeyframe) {
            this.gop = [];
            this.gopBytes = 0;
            this.gopDropped = false;
        }
        if (!this.gopDropped) {
            if (this.gopBytes + fragment.length > MAX_GOP_BYTES) {
                this.gop = [];
                this.gopBytes = 0;
                this.gopDropped = true;
            } else {
                this.gop.push(fragment);
                this.gopBytes += fragment.length;
            }
        }
        this.sinks.forEach((sink) => sink.enqueue(fragment, isKeyframe));
    }

    /** Longest any player has failed to advance while meant to play, in ms. */
    stalledMs(): number {
        let max = 0;
        this.sinks.forEach((sink) => {
            max = Math.max(max, sink.stalledMs());
        });
        return max;
    }

    /** Mark the stream finished; each player ends once its queue drains. */
    end(): void {
        this.ended = true;
        this.sinks.forEach((sink) => sink.enqueue(null));
    }

    /**
     * Seconds the least-buffered playing player is ahead. Paused players only
     * count when nobody plays, so one can't hold back reading for the rest.
     */
    aheadSeconds(): number {
        const all = [...this.sinks];
        const playing = all.filter((sink) => sink.playing);
        const pool = playing.length ? playing : all;
        if (!pool.length) return 0;
        return Math.min(...pool.map((sink) => sink.aheadSeconds()));
    }

    /** Play this feed in `video`; returns the detach function. */
    attach(video: HTMLVideoElement): () => void {
        const sink = new FeedSink(this, video, [this.init, ...this.gop]);
        this.sinks.add(sink);
        return () => {
            sink.close();
            this.sinks.delete(sink);
        };
    }

    close(): void {
        this.closed = true;
        this.gop = [];
        this.sinks.forEach((sink) => sink.close());
        this.sinks.clear();
    }
}
